import { ReactNode } from "react";
import { ComponentWithDefaultProps } from "@/types/utility"
import { cn } from "@/lib/utils";

const SPACING = "w-full container px-3 sm:px-6 md:px-10 lg:px-16 xl:px-24" as const

/**
 * Layout component for application pages.
 * Provides a centered container with optional heading and side content.
 *
 * @param {object} props
 * @param {string} [props.className] - Additional class names for the root div.
 * @param {ReactNode} [props.heading] - Optional heading node.
 * @param {ReactNode} [props.side] - Optional side node (e.g., sidebar).
 * @param {ReactNode} props.children - Main content.
 * @param {React.Ref<HTMLDivElement>} [props.ref] - Ref for the root div.
 */
export function ApplicationPage({
  className,
  children,
  heading,
  ref,
}: ComponentWithDefaultProps<
  HTMLDivElement,
  {
    heading?: ReactNode;
  }
>) {
  return (
    <div
      className={cn("flex items-center justify-center h-full w-full flex-col gap-4 space-y-4 sm:space-y-8 mt-4 sm:mt-16 mb-8 sm:mb-16", className)}
      ref={ref}
    >
      {heading}

      {children}
    </div>
  );
}

/**
 * Side section for the ApplicationPage layout.
 * Typically used for navigation or contextual information.
 *
 * @example
 * ```jsx
 * <div className="flex flex-col gap-6 w-64">
 *   <div className="bg-muted rounded-lg p-4 shadow-sm">
 *     <h2 className="text-sm font-semibold mb-2 text-foreground">Need Help?</h2>
 *     <p className="text-xs text-muted-foreground mb-2">
 *       Contact our compliance team for support or questions about audits.
 *     </p>
 *     <a
 *       href="mailto:compliance@worksite.com"
 *       className="text-xs text-primary hover:underline"
 *     >
 *       compliance@worksite.com
 *     </a>
 *   </div>
 *   <div className="bg-muted rounded-lg p-4 shadow-sm">
 *     <h2 className="text-sm font-semibold mb-2 text-foreground">Recent Updates</h2>
 *     <ul className="text-xs text-muted-foreground space-y-1">
 *       <li>
 *         <span className="font-medium text-foreground">June 2024:</span> New audit checklist released.
 *       </li>
 *       <li>
 *         <span className="font-medium text-foreground">May 2024:</span> Compliance training video added.
 *       </li>
 *     </ul>
 *   </div>
 * </div>
 * ```
 *
 * @param {object} props
 * @param {string} [props.className] - Additional class names.
 * @param {ReactNode} props.children - Side content.
 * @param {React.Ref<HTMLDivElement>} [props.ref] - Ref for the div.
 */
export function ApplicationPageSide({
  className,
  children,
  ref,
}: ComponentWithDefaultProps<HTMLDivElement>) {
  return (
    <div ref={ref} className={cn("sticky top-24 flex-none", className)}>
      {children}
    </div>
  );
}

/**
 * Main content section for the ApplicationPage layout.
 * Used to wrap the primary children/content of the page.
 *
 * @param {object} props
 * @param {string} [props.className] - Additional class names.
 * @param {ReactNode} props.children - Main content.
 * @param {React.Ref<HTMLDivElement>} [props.ref] - Ref for the div.
 */
export function ApplicationPageChildren({
  className,
  children,
  ref,
  side,
}: ComponentWithDefaultProps<HTMLDivElement> & {
    side?: ReactNode;
  }
) {
  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center gap-4",
        SPACING,
        className
      )}
    >
        {side ? (
          <div className="flex gap-8 items-start justify-center">
            {side}

            {children}
          </div>
        ) : (
          children
        )}
    </div>
  );
}

/**
 * Heading section for the ApplicationPage layout.
 * Supports optional pre-heading and sub-heading.
 *
 * @param {object} props
 * @param {string} [props.className] - Additional class names.
 * @param {ReactNode} props.children - Main heading text.
 * @param {ReactNode} [props.preHeading] - Optional pre-heading node.
 * @param {ReactNode} [props.subHeading] - Optional sub-heading node.
 * @param {React.Ref<HTMLDivElement>} [props.ref] - Ref for the div.
 */
export function ApplicationPageHeading({
  className,
  children,
  subHeading,
  preHeading,
  ref,
}: ComponentWithDefaultProps<
  HTMLDivElement,
  {
    preHeading?: ReactNode;
    subHeading?: ReactNode;
  }
>) {
  return (
    <div ref={ref} className={cn("flex flex-col space-y-6", SPACING, className)}>
      <div className="flex flex-col space-y-4">
        {preHeading}

        <h1 className="text-balance text-xl md:text-2xl lg:text-3xl font-semibold text-gray-800 dark:text-foreground">
          {children}
        </h1>
      </div>

      {subHeading}
    </div>
  );
}

/**
 * Pre-heading text for the ApplicationPageHeading.
 * Typically used for section labels or context above the main heading.
 *
 * @param {object} props
 * @param {string} [props.className] - Additional class names.
 * @param {ReactNode} props.children - Pre-heading content.
 * @param {React.Ref<HTMLDivElement>} [props.ref] - Ref for the element.
 */
export function ApplicationPagePreHeading({
  className,
  children,
  ref,
}: ComponentWithDefaultProps<HTMLDivElement>) {
  return (
    <p
      ref={ref}
      className={cn("text-sm font-normal text-muted-foreground", className)}
    >
      {children}
    </p>
  );
}

/**
 * Sub-heading text for the ApplicationPageHeading.
 * Used for additional description or context below the main heading.
 *
 * @param {object} props
 * @param {string} [props.className] - Additional class names.
 * @param {ReactNode} props.children - Sub-heading content.
 * @param {React.Ref<HTMLDivElement>} [props.ref] - Ref for the element.
 * @param {React.ElementType} [props.as] - The element or component to render as. Defaults to 'p'.
 */
export function ApplicationPageSubHeading({
  className,
  children,
  ref,
  as: Component = "p",
}: ComponentWithDefaultProps<HTMLDivElement> & { as?: React.ElementType }) {
  return (
    <Component
      ref={ref}
      className={cn(
        "mt-4 line-clamp-3 text-sm/6 text-muted-foreground max-w-2xl",
        className
      )}
    >
      {children}
    </Component>
  );
}
